import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DialogContent, Typography } from '@material-ui/core';
import { AlertTitle } from '@material-ui/lab';

// STORE
import { AuthActions, SubscriptionActions } from '@/store/actions';

// CONSTANTS
import { LOCATIONS_SUBSCIPTION_FORM_PAGE } from '@/constants/locations';

// COMPONENTS
import Loader from '@/components/Loader';
import RegistrationForm from '../RegistrationForm';

// STYLES
import { AlertStyled, DialogStyled, CloseIcon } from './styles';

const AuthDialog = ({
  disabled,
  errors,
  user,
  partner,
  dictionary,
  registrationLoading,
  authDialogIsOpen,
  authDialogProps,
  authDialogClose,
  addSubscriptionRegistration,
  manageAuthDialogState,
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { action, variant, errorMessage } = authDialogProps;
  const errMessage = errors.registration || errorMessage;
  const hasError = Boolean(errMessage);

  function handleDialogClose() {
    authDialogClose();
  }

  return (
    <DialogStyled fullWidth maxWidth="xs" open={authDialogIsOpen} onClose={handleDialogClose}>
      <DialogContent>
        <CloseIcon size="medium" aria-label="close" onClick={handleDialogClose}>
          <i className="icon-close" />
        </CloseIcon>
        <Typography className="m-b-20" variant="h3" align="center">
          {t(`authDrawer.${variant === 'login' ? 'login.title' : 'registration.title'}`)}
        </Typography>
        {hasError && (
          <AlertStyled icon={false} severity="error" className="sizeSmall">
            <AlertTitle className="p3">{errMessage}</AlertTitle>
          </AlertStyled>
        )}
        <RegistrationForm
          isDialog
          disabled={disabled}
          variant={variant}
          user={user}
          onNext={() => {
            if (action) {
              dispatch(action);
            }

            handleDialogClose();
          }}
          partner={partner}
          dictionary={dictionary}
          addSubscriptionRegistration={addSubscriptionRegistration}
          analyticsLocation={LOCATIONS_SUBSCIPTION_FORM_PAGE}
          manageAuthDialogState={manageAuthDialogState}
        />
      </DialogContent>
      <Loader active={registrationLoading} />
    </DialogStyled>
  );
};

AuthDialog.propTypes = {
  authDialogIsOpen: PropTypes.bool.isRequired,
  authDialogProps: PropTypes.object.isRequired,
  authDialogClose: PropTypes.func.isRequired,
  partner: PropTypes.object,
  dictionary: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  registrationLoading: PropTypes.bool.isRequired,
  addSubscriptionRegistration: PropTypes.func.isRequired,
  manageAuthDialogState: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const mapStateToProps = ({
  partner,
  subscription: { dictionary, loadings, errors, authDialogIsOpen, authDialogProps },
  auth: { user },
}) => ({
  errors,
  dictionary,
  authDialogIsOpen,
  authDialogProps: authDialogProps || {},
  user: user || {},
  partner: partner.data || {},
  registrationLoading: loadings.registration,
});

const mapDispatchToProps = dispatch => {
  return {
    authDialogClose: (props = {}) =>
      dispatch(
        SubscriptionActions.manageSubscriptionAuthDialogState.action({ state: false, props }),
      ),
    addSubscriptionRegistration: data => {
      dispatch(SubscriptionActions.addSubscriptionRegistration.action(data));
    },
    manageAuthDialogState: props =>
      dispatch(AuthActions.manageAuthDialogState.action({ state: true, props })),
  };
};

export default compose(React.memo, connect(mapStateToProps, mapDispatchToProps))(AuthDialog);
